<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import { formatarDataBR } from '@/utils/useDataBR'

const props = defineProps({
  tipoOperacao: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const uiStore = useUiStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'numeroProcessoEmbarque', label: 'Nº Processo', sortable: true, slotName: 'numeroProcesso', class: 'fw-medium text-body-emphasis' },
  { field: 'descricaoEmbarque', label: 'Descrição', sortable: true, slotName: 'descricaoEmbarque' },
  { field: 'lstTipoDocumentoTransporte', label: 'Tp. Doc. Transporte', align: 'center', sortable: true, slotName: 'tipoDocumentoTransporte' },
  { field: 'documentoTransporte', label: 'Nº Doc. Transporte', align: 'center', sortable: true, slotName: 'documentoTransporte' },
  { field: 'lstModalTransporte', label: 'Modal', sortable: true, slotName: 'modal' },
  { field: 'previsaoChegadaETA', label: 'Previsão ETA', align: 'center', sortable: true, slotName: 'previsaoChegadaETA' },
  { field: 'lstStatusEmbarque', label: 'Status', align: 'center', sortable: true, slotName: 'status' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstModalTransporte: (row) => getModalTexto(row.lstModalTransporte),
    lstStatusEmbarque: (row) => getStatusTexto(row.lstStatusEmbarque)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => {
  buscarDados(1)
}

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const listas = reactive({})

const filtros = reactive({
  busca: '',
  lstStatus: ''
})

const temPermissaoCompleta = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('EMBARQUES_COMPLETO')) return true
  if (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')) return true
  // Module-based: verifica se alguma permissão do módulo EMBARQUES indica COMPLETO
  return authStore.getPermsDoModulo('EMBARQUES').some(p => p === 'COMPLETO' || p.includes('COMPLETO'))
})
const temPermissaoVisualizacao = computed(() => authStore.pode('EMBARQUES_VISUALIZACAO'))
const podeVerAcoes = computed(() => temPermissaoCompleta.value || temPermissaoVisualizacao.value)

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
  }
})

// Recarrega dados quando a prop tipoOperacao mudar (ex: alternar entre Importação/Exportação)
watch(() => props.tipoOperacao, async (novoTipo, tipoAnterior) => {
  if (novoTipo !== tipoAnterior) {
    lista.value = []
    paginacao.value.TotalCount = 0
    await carregarListas()
    buscarDados(1)
  }
})

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['Embarque'])
    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.busca) params.append('termo', filtros.busca)
    if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)
    if (props.tipoOperacao) params.append('lstTipoOperacaoComex', props.tipoOperacao)

    const response = await api.get(`/Embarques?${params.toString()}`)
    lista.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar os embarques.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.busca = ''
  filtros.lstStatus = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarItem = async (item) => {
  try {
    loading.value = true
    const response = await api.get(`/Embarques/${item.idEmbarque}`)
    const data = response.data
    // Enriquece o objeto com tipoOperacao para que o cadastro saiba de onde veio
    if (props.tipoOperacao) {
      data._tipoOperacao = props.tipoOperacao
    }
    emit('editar-item', data)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados do embarque.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idEmbarque
  auditoriaSelecionada.nome = item.numeroProcessoEmbarque || `Embarque #${item.idEmbarque}`
  mostrarAuditoria.value = true
}

const getStatusTexto = (idStatus) => {
  const idStr = String(idStatus)
  const statusObj = (listas.LstStatusEmbarque || []).find(s => String(s.idListaClienteItem) === idStr)
  return statusObj ? statusObj.textoLista : idStatus
}

const getModalTexto = (idModal) => {
  const idStr = String(idModal)
  const modalObj = (listas.LstModalTransporte || []).find(m => String(m.idListaClienteItem) === idStr)
  return modalObj ? modalObj.textoLista : idModal
}

const getTipoDocTexto = (idTipoDoc) => {
  const idStr = String(idTipoDoc)
  const tipoObj = (listas.LstTipoDocumentoTransporte || []).find(t => String(t.idListaClienteItem) === idStr)
  return tipoObj ? tipoObj.textoLista : idTipoDoc
}

const obterCorStatus = (idStatus) => {
  const texto = String(getStatusTexto(idStatus)).toUpperCase()

  if (texto.includes('CONCLUÍDO') || texto.includes('FINALIZADO') || texto.includes('ENTREGUE')) {
    return 'badge-ghost-success'
  }
  if (texto.includes('ABERTO') || texto.includes('PENDENTE') || texto.includes('TRÂNSITO')) {
    return 'badge-ghost-warning'
  }
  if (texto.includes('CANCELADO')) {
    return 'badge-ghost-danger'
  }
  if (texto.includes('ANÁLISE') || texto.includes('PLANEJADO')) {
    return 'badge-ghost-info'
  }

  return 'badge-ghost-secondary'
}

onMounted(async () => {
  await carregarListas()
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    buscarDados(1)
  }
})

onActivated(async () => {
  await carregarListas()
  buscarDados(paginacao.value.CurrentPage || 1)
})

const showModalCriarEmbarque = ref(false)
const descricaoEmbarque = ref('')
const lstTipoOperacao = ref('')
const modalCriarLoading = ref(false)

const abrirModalCriarEmbarque = () => {
  descricaoEmbarque.value = ''
  lstTipoOperacao.value = props.tipoOperacao || ''
  modalCriarLoading.value = false
  showModalCriarEmbarque.value = true
}

const confirmarCriarEmbarque = async () => {
  if (!descricaoEmbarque.value?.trim()) {
    emit('show-notification', { message: 'A descrição do embarque é obrigatória.', type: 'warning' })
    return
  }
  if (!lstTipoOperacao.value) {
    emit('show-notification', { message: 'O Tipo de Operação é obrigatório.', type: 'warning' })
    return
  }

  modalCriarLoading.value = true
  try {
    const payload = {
      descricaoEmbarque: descricaoEmbarque.value.trim(),
      lstTipoOperacaoComex: lstTipoOperacao.value,
      idFilial: authStore.empresaAtiva ? Number(authStore.empresaAtiva) : null,
      lstStatusEmbarque: 'LOG_ST_EMBARQUE6'
    }

    const res = await api.post('/Embarques', payload)
    const novoEmbarque = res.data

    emit('show-notification', {
      message: `Embarque criado com sucesso! Nº Processo: ${novoEmbarque.numeroProcessoEmbarque}`,
      type: 'success'
    })

    showModalCriarEmbarque.value = false
    emit('editar-item', novoEmbarque)
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao criar embarque.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    modalCriarLoading.value = false
  }
}

defineExpose({
  buscarDados
})
</script>

<template>
  <PaginaAdministrar
    :titulo="uiStore.currentModuleTitle || 'Gestão de Embarques'"
    subtitulo="Administração de processos logísticos, bookings e documentos de transporte"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="abrirModalCriarEmbarque"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-5">
          <label class="form-label">Busca Geral</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Processo, Booking ou Documento..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-4">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos os Status</option>
            <option v-for="s in (listas.LstStatusEmbarque || [])" :key="s.idListaClienteItem" :value="s.idListaClienteItem">
              {{ s.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-auto ms-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum embarque encontrado."
      row-key="idEmbarque"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarItem($event.row)"
    >
      <template #modal="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ getModalTexto(row.lstModalTransporte) }}
        </span>
      </template>

      <template #numeroProcesso="{ row }">
        <span class="fw-medium text-body-emphasis">{{ row.numeroProcessoEmbarque || '-' }}</span>
      </template>

      <template #descricaoEmbarque="{ row }">
        <span>{{ row.descricaoEmbarque || '-' }}</span>
      </template>

      <template #tipoDocumentoTransporte="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ getTipoDocTexto(row.lstTipoDocumentoTransporte) }}
        </span>
      </template>

      <template #documentoTransporte="{ row }">
        <span v-if="row.numeroDocumentoTransporte" class="fw-medium d-block">
          {{ row.numeroDocumentoTransporte }}
        </span>
        <span v-else class="text-muted">-</span>
      </template>

      <template #previsaoChegadaETA="{ row }">
        {{ formatarDataBR(row.previsaoChegadaETA) }}
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterCorStatus(row.lstStatusEmbarque)">{{ getStatusTexto(row.lstStatusEmbarque) }}</span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon" @click="editarItem(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="Embarque"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    :tabelas-filhas="['EmbarqueFatura', 'EmbarqueItem']"
    chave-estrangeira="IdEmbarque"
    @fechar="mostrarAuditoria = false"
  />
  <!-- MODAL DE CRIAÇÃO RÁPIDA DE EMBARQUE -->
  <ModalFormularioV2
    v-model="showModalCriarEmbarque"
    titulo="Novo Embarque"
    tamanho="sm"
    cor-header="var(--bs-warning)"
    :loading="modalCriarLoading"
    @confirmar="confirmarCriarEmbarque"
  >
    <div class="text-center mb-3">
      <i class="bi bi-ship fs-1 text-warning"></i>
      <p class="text-muted mt-2 mb-0">Informe uma descrição para identificar o embarque.</p>
    </div>
    <CampoInput
      id="descricaoEmbarque"
      label="Descrição do Embarque"
      v-model="descricaoEmbarque"
      placeholder="Ex: Importação de Seringas 10ml - USA"
      required
      autofocus
    />
    <CampoSelect
      id="lstTipoOperacao"
      label="Tipo de Operação"
      v-model="lstTipoOperacao"
      :options="listas.LstTipoOperacaoComex || []"
      option-label="textoLista"
      option-value="idListaClienteItem"
      required
      placeholder="Selecione o tipo..."
    />
    <small class="text-muted">
      <i class="bi bi-info-circle me-1"></i>
      O Nº do Processo Interno será gerado automaticamente com base no tipo de operação.
    </small>
  </ModalFormularioV2>
</template>
